<script setup>
import { computed, onMounted, reactive, ref } from 'vue';

const props = defineProps({
    token: { type: String, required: true },
    quote: { type: Object, required: true },
    companyContact: { type: Object, default: () => ({}) },
    taxLocationReady: { type: Boolean, default: false },
});

const form = reactive({
    first_name: props.companyContact.first_name || '',
    last_name: props.companyContact.last_name || '',
    company_name: props.companyContact.company_name || '',
    address1: props.companyContact.address1 || '',
    address2: '',
    city: props.companyContact.city || '',
    state: props.companyContact.state || '',
    zip: props.companyContact.zip || '',
    country: props.companyContact.country || 'SE',
    phone: props.companyContact.phone || '',
    email: props.companyContact.email || '',
});

const tldRequirements = reactive({
    nicse_id_number: '',
});

const presets = reactive({
    myflow_site: true,
    myflow_email: true,
});

const submitting = ref(false);
const isOpeningBilling = ref(false);
const errorMessage = ref(null);
const billingPortalRequired = ref(!props.taxLocationReady);

const domain = props.quote.domain;
const years = props.quote.years;
const priceEur = (((props.quote.quote?.purchase_price_eur_cents || 0) * years) / 100).toFixed(2);
const renewalEur = ((props.quote.quote?.renewal_price_eur_cents || 0) / 100).toFixed(2);
const isPremium = !!props.quote.quote?.is_premium;
const requiresNicSeIdNumber = computed(() =>
    ['.se', '.nu'].some((tld) =>
        String(domain || '')
            .toLowerCase()
            .endsWith(tld)
    )
);
const hasNicSeIdNumberInput = computed(() => String(tldRequirements.nicse_id_number || '').trim().length > 0);
const nicSeIdNumberLabel = computed(() => (String(form.company_name || '').trim().length > 0 ? _mft('setting:domains.buy.review.nicSeOrganisationNumber') : _mft('setting:domains.buy.review.nicSePersonalNumber')));

const hasValidNicSeIdNumber = computed(() => {
    if (!requiresNicSeIdNumber.value || !hasNicSeIdNumberInput.value) {
        return true;
    }

    return /^\d{6}-\d{4}$/.test(String(tldRequirements.nicse_id_number || ''));
});

const nicSeIdNumberError = computed(() => {
    if (!requiresNicSeIdNumber.value || !hasNicSeIdNumberInput.value || hasValidNicSeIdNumber.value) {
        return '';
    }

    return _mft('setting:domains.buy.review.nicSeIdNumberInvalid');
});

const formValidates = computed(() => {
    const requiredFields = [form.first_name, form.last_name, form.address1, form.city, form.state, form.zip, form.country, form.phone, form.email];

    if (requiresNicSeIdNumber.value) {
        requiredFields.push(tldRequirements.nicse_id_number);
    }

    const hasRequiredFields = requiredFields.every((value) => String(value || '').trim().length > 0);
    const hasValidCountry = /^[A-Z]{2}$/i.test(String(form.country || '').trim());
    const hasValidPhone = /^\+[0-9]/.test(String(form.phone || '').trim());
    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || '').trim());

    return hasRequiredFields && hasValidCountry && hasValidPhone && hasValidEmail && hasValidNicSeIdNumber.value;
});

const submitButtonLabel = computed(() => {
    if (submitting.value) {
        return _mft('setting:domains.buy.review.processing');
    }

    return _mft('setting:domains.buy.review.payAndRegister', {
        price: priceEur,
    });
});

const onSubmit = async () => {
    if (!formValidates.value || billingPortalRequired.value) {
        return;
    }

    submitting.value = true;
    errorMessage.value = null;

    const selectedPresets = Object.entries(presets)
        .filter(([, v]) => v)
        .map(([k]) => k);

    const tldRequirementsPayload = {};
    if (requiresNicSeIdNumber.value) {
        tldRequirementsPayload['X-NICSE-IDNUMBER'] = tldRequirements.nicse_id_number.trim();
    }

    try {
        const { data } = await axios.post(route('settings.domains.buy.confirm', props.token), {
            registrant: form,
            tld_requirements: tldRequirementsPayload,
            presets: selectedPresets,
        });
        window.location.href = data.redirect;
    } catch (e) {
        if (e?.response?.data?.billing_portal_required) {
            billingPortalRequired.value = true;
        }
        errorMessage.value = e?.response?.data?.message || _mft('setting:domains.buy.review.purchaseFailed');
    } finally {
        submitting.value = false;
    }
};

const openBillingPortal = async () => {
    if (isOpeningBilling.value) return;

    isOpeningBilling.value = true;
    errorMessage.value = null;

    const returnUrl = new URL(route('settings.domains.buy.review', props.token), window.location.origin).href;

    try {
        const { data } = await axios.post(route('billing.subscription.portal'), { return_url: returnUrl });

        if (data?.url) {
            // Same-tab navigation, matching onSubmit above. window.open with a
            // features string can be treated as a popup — and this runs after an
            // await, outside the user-gesture window, so it would be blocked.
            window.location.href = data.url;
            return;
        }

        errorMessage.value = _mft('setting:domains.buy.review.openBillingFailed');
    } catch (e) {
        errorMessage.value = e?.response?.data?.error || _mft('setting:domains.buy.review.openBillingFailed');
    } finally {
        isOpeningBilling.value = false;
    }
};

onMounted(() => {
    let firstName = window.myflow.user.name.split(' ')[0];
    let restOfName = window.myflow.user.name.split(' ').slice(1).join(' ');

    form.first_name = firstName;
    form.last_name = restOfName;
});
</script>

<template>
    <div>
        <frosted-bar
            :breadcrumbs="[
                {
                    url: route('settings.company.settings.domains'),
                    label: _mft('setting:domains.allDomains'),
                },
                {
                    url: route('settings.domains.buy.start'),
                    label: _mft('setting:domains.buy.review.searchDomains'),
                },
                { label: _mft('setting:domains.buy.review.confirmPurchase') },
            ]"
        >
            <h1>
                {{
                    _mft('setting:domains.buy.review.purchaseDomain', {
                        domain,
                    })
                }}
            </h1>
        </frosted-bar>
        <div class="mx-auto w-full max-w-3xl space-y-6 p-6">
            <div class="space-y-1">
                <h1 class="text-2xl font-semibold">
                    {{ _mft('setting:domains.buy.review.confirmPurchase') }}
                </h1>
                <p class="text-sm text-gray-600">
                    {{ _mft('setting:domains.buy.review.contactInfoIntro') }}
                </p>
            </div>

            <card-basic>
                <div class="flex items-baseline justify-between">
                    <div>
                        <div class="text-lg font-semibold">{{ domain }}</div>
                        <div class="text-sm text-gray-500">
                            {{ _mft('setting:domains.buy.review.registrationTerm', { years }) }}
                            ·
                            {{ _mft('setting:domains.buy.review.renewsYearly') }}
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-2xl font-bold">€{{ priceEur }}</div>
                        <div class="text-xs text-gray-500">
                            {{
                                _mft('setting:domains.buy.review.thenPerYear', {
                                    price: renewalEur,
                                })
                            }}
                        </div>
                    </div>
                </div>
                <p class="mt-3 text-xs text-gray-500">
                    {{ _mft('setting:domains.buy.review.vatNotice') }}
                </p>
                <p v-if="isPremium" class="mt-3 rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800">
                    {{ _mft('setting:domains.buy.review.premiumRenewalNotice', { price: renewalEur }) }}
                </p>
            </card-basic>

            <div :class="billingPortalRequired ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-gray-200 bg-gray-50 text-gray-700'" class="rounded-md border p-4 text-sm">
                <p class="font-medium">
                    {{ billingPortalRequired ? _mft('setting:domains.buy.review.billingRequiredTitle') : _mft('setting:domains.buy.review.billingReadyTitle') }}
                </p>
                <p class="mt-1 text-xs">
                    {{ billingPortalRequired ? _mft('setting:domains.buy.review.billingRequiredDescription') : _mft('setting:domains.buy.review.billingReadyDescription') }}
                </p>
                <button type="button" class="mf-link mt-2 font-medium" :disabled="isOpeningBilling" @click="openBillingPortal">
                    {{ isOpeningBilling ? _mft('setting:domains.buy.review.openingBilling') : _mft('setting:domains.buy.review.reviewBilling') }}
                </button>
            </div>

            <form class="space-y-6" @submit.prevent="onSubmit">
                <card-basic>
                    <h2 class="text-base font-semibold">
                        {{ _mft('setting:domains.buy.review.domainOwner') }}
                    </h2>
                    <p class="mt-1 text-xs text-gray-500">
                        {{ _mft('setting:domains.buy.review.domainOwnerDescription') }}
                    </p>
                    <div class="mt-4 grid grid-cols-2 gap-4">
                        <input-text :label="_mft('setting:domains.buy.review.firstName')" auto-complete="given-name" v-model:content="form.first_name" required />
                        <input-text :label="_mft('setting:domains.buy.review.lastName')" auto-complete="family-name" v-model:content="form.last_name" required />
                        <div class="col-span-2">
                            <input-text :label="_mft('setting:domains.buy.review.companyOptional')" v-model:content="form.company_name" />
                        </div>
                        <div class="col-span-2">
                            <input-email :label="_mft('setting:domains.buy.review.email')" auto-complete="email" v-model:content="form.email" required />
                        </div>
                        <div class="col-span-2">
                            <input-text :label="_mft('setting:domains.buy.review.phone')" v-model:content="form.phone" inputtype="tel" pattern="\+[0-9].*" auto-complete="tel" required />
                        </div>
                        <div class="col-span-2">
                            <input-text :label="_mft('setting:domains.buy.review.streetAddress')" auto-complete="street-address" v-model:content="form.address1" required />
                        </div>
                        <input-text :label="_mft('setting:domains.buy.review.city')" v-model:content="form.city" required />
                        <input-text :label="_mft('setting:domains.buy.review.postalCode')" auto-complete="postal-code" v-model:content="form.zip" required />
                        <input-text :label="_mft('setting:domains.buy.review.stateRegion')" v-model:content="form.state" required />
                        <input-text :label="_mft('setting:domains.buy.review.country')" autocomplete="off" v-model:content="form.country" required maxlength="2" />
                    </div>
                    <div v-if="requiresNicSeIdNumber" class="mt-4">
                        <input-text
                            :label="nicSeIdNumberLabel"
                            v-model:content="tldRequirements.nicse_id_number"
                            :error="nicSeIdNumberError"
                            pattern="[0-9]{6}-[0-9]{4}"
                            placeholder="xxxxxx-xxxx"
                            autocomplete="off"
                            required
                            maxlength="11"
                            class="!mb-0"
                        />
                        <p class="text-xs text-gray-500">
                            {{ _mft('setting:domains.buy.review.nicSeIdNumberDescription') }}
                        </p>
                    </div>
                </card-basic>

                <card-basic>
                    <h2 class="text-base font-semibold">
                        {{ _mft('setting:domains.buy.review.domainPurpose') }}
                    </h2>
                    <p class="mt-1 text-xs text-gray-500">
                        {{ _mft('setting:domains.buy.review.domainPurposeDescription', { domain }) }}
                        <strong>{{ _mft('setting:domains.buy.review.noAdditionalConfiguration') }}</strong
                        ><br />
                        {{ _mft('setting:domains.buy.review.changeSettingsLater') }}
                    </p>
                    <div class="mt-3 rounded-md border p-3">
                        <input-checkbox :label="_mft('setting:domains.presets.myflowSite.label')" v-model:content="presets.myflow_site" />
                        <p class="ml-8 -mt-2 text-xs text-gray-400">
                            {{ _mft('setting:domains.buy.review.myflowSiteDescription', { domain }) }}
                        </p>
                    </div>
                    <div class="mt-2 rounded-md border p-3">
                        <input-checkbox :label="_mft('setting:domains.presets.myflowEmail.label')" v-model:content="presets.myflow_email" />
                        <p class="ml-8 -mt-2 text-xs text-gray-400">
                            {{ _mft('setting:domains.buy.review.myflowEmailDescription', { domain }) }}
                        </p>
                    </div>
                </card-basic>

                <div v-if="errorMessage" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {{ errorMessage }}
                </div>

                <div class="flex items-center justify-between">
                    <a :href="route('settings.domains.buy.start')" class="text-sm text-gray-600 mf-link">← {{ _mft('setting:domains.buy.review.backToSearch') }}</a>
                    <div class="flex flex-col items-end gap-1">
                        <button-base :style-type="submitting || billingPortalRequired ? 'disabled' : formValidates ? 'success' : 'disabled'" type="submit" :disabled="submitting || billingPortalRequired || !formValidates">
                            {{ submitButtonLabel }}
                        </button-base>
                        <p v-if="!billingPortalRequired && !formValidates && !submitting" class="text-right text-xs text-gray-500">
                            {{ _mft('setting:domains.buy.fillRequiredFieldsToContinue') }}
                        </p>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>
